import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { technologyHighlights, technologyLogos } from '../../data/mockData'; // Update path if needed

const TechnologySection = () => {
  const targetRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Scroll tracking for the main header fade-out
  const { scrollYProgress: headerScrollYProgress } = useScroll({
    target: targetRef,
    offset: ["start start", "end start"],
  });

  // Scroll tracking for the inner content animations
  const { scrollYProgress: contentScrollYProgress } = useScroll({
    target: contentRef,
    offset: ['start start', 'end end']
  });

  // --- Animation Definitions ---

  const headerOpacity = useTransform(headerScrollYProgress, [0, 0.15], [1, 0]);
  const headerScale = useTransform(headerScrollYProgress, [0, 0.15], [1, 0.95]);
  const mainContentOpacity = useTransform(headerScrollYProgress, [0.15, 0.25], [0, 1]);

  // Opacity for the 3 images in the right panel
  const image1Opacity = useTransform(contentScrollYProgress, [0, 0.30, 0.33], [1, 1, 0]);
  const image2Opacity = useTransform(contentScrollYProgress, [0.33, 0.36, 0.63, 0.66], [0, 1, 1, 0]);
  const image3Opacity = useTransform(contentScrollYProgress, [0.66, 0.69, 1], [0, 1, 1]);
  const imageOpacities = [image1Opacity, image2Opacity, image3Opacity];

  // --- Highlighting Logic: Text Color Fade ---
  // Animate from gray (#6B7280) to white (#FFFFFF)
  const text1Color = useTransform(contentScrollYProgress, [0.33, 0.36], ["#FFFFFF", "#6B7280"]);
  const text2Color = useTransform(contentScrollYProgress, [0.33, 0.36, 0.66, 0.69], ["#6B7280", "#FFFFFF", "#FFFFFF", "#6B7280"]);
  const text3Color = useTransform(contentScrollYProgress, [0.66, 0.69], ["#6B7280", "#FFFFFF"]);
  const textColors = [text1Color, text2Color, text3Color];

  // Appearance animation for the 2nd and 3rd text blocks
  const text2Opacity = useTransform(contentScrollYProgress, [0.3, 0.36], [0, 1]);
  const text2Y = useTransform(contentScrollYProgress, [0.3, 0.36], [30, 0]);
  const text3Opacity = useTransform(contentScrollYProgress, [0.63, 0.69], [0, 1]);
  const text3Y = useTransform(contentScrollYProgress, [0.63, 0.69], [30, 0]);

  return (
    <section ref={targetRef} id="technology" className="relative bg-primary-secondary h-[300vh]">
      
      <div className="sticky top-0 h-screen flex items-center justify-center">
        <motion.div
          style={{ opacity: headerOpacity, scale: headerScale }}
          className="text-center container mx-auto px-6"
        >
          <h2 className="text-4xl lg:text-6xl font-display font-bold text-white mb-6">
            Technology
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-12">
            We leverage an ensemble of high-frequency, diverse data streams with custom neural networks that continuously retrain on grid dynamics
            providing a             <br /><span className="text-primary-cyan font-semibold">dynamic analytical advantage</span>.
          </p>
          
          {/* Technology Logos */}
          <div className="flex flex-wrap items-center justify-center gap-8 lg:gap-12">
            {technologyLogos.map((logo) => {
              const isTensorFlowOrPyTorch = logo.name === 'TensorFlow' || logo.name === 'PyTorch';
              return (
                <img
                  key={logo.name}
                  src={logo.src}
                  alt={logo.name}
                  className={`h-24 lg:h-16 w-auto grayscale object-contain ${isTensorFlowOrPyTorch ? 'opacity-[0.8]' : 'opacity-[0.5]'}`}
                  style={{ filter: 'grayscale(100%)' }}
                />
              );
            })}
          </div>
        </motion.div>
      </div>

      <div className="relative z-10 -mt-[100vh]">
        <div className="h-screen" />

        <div ref={contentRef} className="relative h-[200vh]">
          <div className="sticky top-0 h-screen">
            <motion.div style={{ opacity: mainContentOpacity }} className="container mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 h-full">

              <div className="flex flex-col justify-center">
                <div className="space-y-16">
                  {technologyHighlights.map((tech, index) => (
                    <motion.div
                      key={tech.title}
                      // Apply reveal animation only to the 2nd and 3rd items
                      style={{
                        opacity: index > 0 ? (index === 1 ? text2Opacity : text3Opacity) : 1,
                        y: index > 0 ? (index === 1 ? text2Y : text3Y) : 0,
                      }}
                    >
                      {/* Apply the animated color to the text container */}
                      <motion.div style={{ color: textColors[index] }} className="space-y-3">
                        <h3 className="text-2xl font-semibold text-current transition-colors duration-300">{tech.title}</h3>
                        <p className="max-w-lg leading-relaxed text-current transition-colors duration-300 opacity-80">{tech.description}</p>
                      </motion.div>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="hidden lg:flex items-center justify-center">
                <div className="relative w-full h-3/4 rounded-3xl overflow-hidden border border-white/10 bg-zinc-900/50">
                  {technologyHighlights.map((tech, index) => (
                    <motion.img
                      key={tech.image}
                      src={tech.image}
                      alt={tech.title}
                      style={{ opacity: imageOpacities[index] }}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  ))}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                </div>
              </div>

            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechnologySection;